import { Dropdown, Grid } from 'antd';
import { MenuOutlined } from '@ant-design/icons';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/auth';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { $ } from '../lib/api';
import { toast } from '../lib/toast';
import { notificationSound } from '../utils/notificationSound';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useShortcuts } from '../lib/hooks';
import { useImRealtime } from '../hooks/useImRealtime';
import { NewCustomerModal } from '../components/NewCustomerModal';
import { GlobalSearch } from '../components/GlobalSearch';
// v1.5 阶段 4 P2：菜单按权限点 filter（替代 role 硬编码）
// 权限点 = 后端 @RequirePermissions 实际用的 code（5-A 12 边界审过 47 项映射）
import { useHasPermission, useHasAnyPermission, usePermissions } from '../lib/permissions';

interface NavLink {
  key: string;
  label: string;
  icon: ReactNode;
  count?: number;
  /** true=未读红底白字圆 badge(微信式·新消息脉冲一次)；否则灰色计数 .nav-count。 */
  unread?: boolean;
  hide?: boolean;
  /** v1.5 P2：菜单显示要求的权限点（OR 数组= any，单 code= AND）；不填=始终显示 */
  requirePerm?: string | string[];
  requireAny?: string[];
}

const Icon = {
  home: <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M3 12 12 3l9 9M5 10v10h14V10" /></svg>,
  users: <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>,
  pool: <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M2 12c2-2 4-2 6 0s4 2 6 0 4-2 6 0M2 18c2-2 4-2 6 0s4 2 6 0 4-2 6 0M2 6c2-2 4-2 6 0s4 2 6 0 4-2 6 0" /></svg>,
  team: <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /></svg>,
  chart: <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" /></svg>,
  robot: <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="3" y="11" width="18" height="10" rx="2" /><circle cx="12" cy="5" r="2" /><path d="M12 7v4M8 16h.01M16 16h.01" /></svg>,
  alert: <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>,
  audit: <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><path d="M9 15l2 2 4-4" /></svg>,
  settings: <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></svg>,
  bell: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>,
  message: <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" /></svg>,
  plus: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14" /></svg>,
  calendar: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></svg>,
  search: <svg className="topbar-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>,
};

// v1.5 P2 #3-#10：8 stages 各自按权限点 filter
// 注：#8 shipments 后端无 perm（用户拍板 A：保留 role 判断 + backlog 补 shipment:view）
const STAGES: Array<{
  num: number; name: string; match: string; endpoint: string;
  requirePerm?: string; hideForRoles?: string[];
}> = [
  { num: 1, name: '客户管理', match: '/customers', endpoint: '/customers', requirePerm: 'customer:view' },
  { num: 2, name: '跟进记录', match: '/follow-ups', endpoint: '/follow-ups', requirePerm: 'follow_up:view' },
  { num: 3, name: '签订合同', match: '/contracts', endpoint: '/contracts', requirePerm: 'contract:view' },
  // 流转理顺：步号顺正 ④收款 ⑤发票（先收钱再开票）
  { num: 4, name: '收款', match: '/payments', endpoint: '/payments', requirePerm: 'payment:view' },
  { num: 5, name: '发票', match: '/invoices', endpoint: '/invoices', requirePerm: 'invoice:view' },
  // #8 shipments 无 perm，保留 role：admin/director/manager/sales 可见，academic 不可见
  { num: 6, name: '教材邮寄', match: '/shipments', endpoint: '/shipments', hideForRoles: ['academic', 'academic_director'] },
  { num: 7, name: '学员资料', match: '/materials', endpoint: '/materials', requirePerm: 'material:view' },
  { num: 8, name: '提交订单', match: '/orders', endpoint: '/orders', requirePerm: 'order:view' },
];

/** IM5：把会话最后一条消息按类型转成预览短文（图片/文件不显 URL）。 */
function imPreview(c: any): string {
  const t = c?.last_type;
  if (t === 'image') return '[图片]';
  if (t === 'file') return '[文件]';
  const s = String(c?.last_preview ?? '').slice(0, 24);
  return s || '[新消息]';
}

export function AppLayout() {
  const user = useAuthStore((s) => s.user)!;
  const logout = useAuthStore((s) => s.logout);
  const [avatarErr, setAvatarErr] = useState(false);
  const nav = useNavigate();
  const qc = useQueryClient();
  const loc = useLocation();
  const path = loc.pathname;
  // v1.5 P2：权限点判断（与后端 @RequirePermissions 对齐）
  const perms = usePermissions();
  const canCreate = useHasPermission('customer:create'); // P2 #47：替代原 role 硬编码
  const [showNewCustomer, setShowNewCustomer] = useState(false);
  // P2 移动端侧边栏抽屉化：<768px 用汉堡键开关 .sidebar 的 off-canvas 抽屉（CSS 已就绪 .sidebar/.sidebar.open）；≥768px 此状态不参与布局、sidebar 仍是常驻列。
  const [drawerOpen, setDrawerOpen] = useState(false);
  const screens = Grid.useBreakpoint(); // antd 断点；md=768px。screens.md 为 true 即 ≥768px。
  const isMobile = screens.md === false; // 注意：首帧 screens 可能为空对象，=== false 才算确定的移动端，避免 SSR/首帧误判
  // 路由切换后自动收起抽屉（点菜单跳页后抽屉应关闭）
  useEffect(() => { setDrawerOpen(false); }, [path]);
  // 回到桌面宽度时强制关闭抽屉态，避免桌面端残留 open 类（虽 ≥768px CSS 不读 .open，仍清状态保持干净）
  useEffect(() => { if (!isMobile && drawerOpen) setDrawerOpen(false); }, [isMobile, drawerOpen]);
  // v1.2 Batch 2-C：侧栏折叠状态 localStorage 持久化
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    try {
      const raw = localStorage.getItem('huanke-sidebar-groups');
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  });
  useEffect(() => {
    try { localStorage.setItem('huanke-sidebar-groups', JSON.stringify(openGroups)); } catch {}
  }, [openGroups]);

  // Global open hook — N shortcut and topbar + button both dispatch this event
  useEffect(() => {
    if (!canCreate) return;
    const handler = () => setShowNewCustomer(true);
    window.addEventListener('huanke:open-new-customer', handler);
    return () => window.removeEventListener('huanke:open-new-customer', handler);
  }, [canCreate]);

  // v1.2-rc2 #4：academic / academic_director 是否属于销售口（决定隐藏销售菜单）
  // ⚠️ 注意：必须放在销售域 useQuery 之前，让 enabled 标志生效
  const isAcademic = user.role === 'academic' || user.role === 'academic_director';

  const { data: unread } = useQuery({
    queryKey: ['notifications-unread'],
    queryFn: () => $.get<{ count: number }>('/notifications/unread-count'),
    refetchInterval: 30_000,
  });

  // P2-7 IM 未读总数 → 侧栏「消息」菜单红徽章
  const { data: imUnreadRaw } = useQuery({
    queryKey: ['im-unread-total'],
    enabled: perms.has('im:view'),
    queryFn: () => $.get<any>('/im/unread-count'),
    refetchInterval: 30_000,
  });
  const imUnreadCount = typeof imUnreadRaw === 'number' ? imUnreadRaw : (imUnreadRaw?.count ?? 0);
  // 「消息」菜单角标 = IM 私信未读 + 系统通知未读 合并总数（程伟拍板：私信+通知合并；右上角铃铛维持只显通知、不动）。
  const notifUnreadCount = unread?.count ?? 0;
  const combinedUnread = imUnreadCount + notifUnreadCount;
  // im12：全局 IM 实时通知（独立通知专用 socket，AppLayout 顶层调一次）——
  //   任何页/最小化都即时收到新消息提示 + 全局 document.title (N) 前缀；返回 socket 在线状态供下方轮询兜底守卫。
  const { connected: imSocketConnected } = useImRealtime(combinedUnread);
  // 新消息/通知全局应用内提示：Layout 常驻、每 30s 轮询(且 Messenger socket 收到新消息会 invalidate 这两个 query → 立即刷新)。
  //   检测合并未读「涨值」弹 toast；★基线就绪前(两个 query 首次加载)不弹★，避免登录/刷新时 0→N 误报；只增不减。
  const imApplicable = perms.has('im:view');
  const unreadDataReady = unread !== undefined && (!imApplicable || imUnreadRaw !== undefined);
  const prevUnreadRef = useRef<number | null>(null);
  const [imPulse, setImPulse] = useState(false); // 新消息到达 →「消息」菜单行脉冲一次
  useEffect(() => {
    if (!unreadDataReady) return;
    const prev = prevUnreadRef.current;
    // im12：socket 在线时本轮询降级为【断线兜底】——由通知专用 socket(useImRealtime) 主导声音+toast，
    //   这里 return 守卫避免与 socket 通道双弹双响(仍更新基线，断线后涨值检测继续生效)。
    if (imSocketConnected) { prevUnreadRef.current = combinedUnread; return; }
    // 在 /im(消息)页时不弹 toast（用户正盯着消息+有提示音、避免活跃聊天被打扰）；但仍更新基线，避免离开页时误弹累积量。
    if (prev !== null && combinedUnread > prev && !path.startsWith('/im')) {
      const delta = combinedUnread - prev;
      notificationSound.play('message'); // 声音全局化：非/im页未读涨值响一次(全局1s debounce去重)；/im页不响(用户在看)
      setImPulse(true);
      // IM5 微信式富提示：涨值瞬间补拉一次会话列表(非常驻轮询、量级可忽略)，拼"发送人：预览"+点击跳会话；
      //   多个会话有新未读→聚合"X 个会话 N 条新消息"跳列表；纯系统通知涨值(无 IM 未读)→回退通用文案。
      (async () => {
        try {
          const convs = await qc.fetchQuery<any[]>({
            queryKey: ['im-conversations'],
            queryFn: () => $.get<any[]>('/im/conversations'),
            staleTime: 0,
          });
          const unreadConvs = (convs ?? [])
            .filter((c) => (c?.unread ?? 0) > 0 && !c?.muted) // 评审:排静音会话(与 /im/unread-count 的 muted=0 口径一致，免弹静音会话/漏弹)
            .sort((a, b) => String(b?.last_message_at ?? '').localeCompare(String(a?.last_message_at ?? '')));
          // 评审:多会话条数用【会话列表 IM 真实未读和】，不用 delta(delta 含系统通知、会把"1 条系统通知"误显成"N 条")
          const imUnreadSum = unreadConvs.reduce((s, c) => s + (c?.unread ?? 0), 0);
          if (unreadConvs.length === 0) {
            toast.info(`📨 你有 ${delta} 条新消息/通知，点左侧「消息」查看`, 5000);
          } else if (unreadConvs.length === 1) {
            const top = unreadConvs[0];
            const who = top?.last_from_name || top?.name || top?.partner?.realName || '新消息';
            toast.imMessage(`${who}：${imPreview(top)}`, () => nav(`/im?conv=${top.id}`), 5000);
          } else {
            toast.imMessage(`${unreadConvs.length} 个会话 ${imUnreadSum} 条新消息`, () => nav('/im'), 5000);
          }
        } catch {
          toast.info(`📨 你有 ${delta} 条新消息/通知，点左侧「消息」查看`, 5000);
        }
      })();
    }
    prevUnreadRef.current = combinedUnread;
  }, [combinedUnread, unreadDataReady, path, qc, nav, imSocketConnected]);
  // 脉冲一次即停：置真后 1s 自动复位（再来新消息重新触发；不长期闪烁晃眼）。
  useEffect(() => {
    if (!imPulse) return;
    const t = setTimeout(() => setImPulse(false), 1000);
    return () => clearTimeout(t);
  }, [imPulse]);

  /** Per-stage counts shown in the sidebar
   *  v1.5 修复 C：按权限点 enabled（替代单一 academic 判断）
   *  - 没 customer:view 的角色（academic 无此权限）不发销售域请求
   *  - 双向收紧：避免 academic 调销售接口 + 避免 sales 调教务接口 */
  const canViewSalesDomain = perms.has('customer:view');
  const { data: stageCounts } = useQuery({
    queryKey: ['sidebar-stage-counts'],
    enabled: canViewSalesDomain,
    queryFn: async () => {
      const counts: Record<string, number> = {};
      await Promise.all(STAGES.map(async (s) => {
        try {
          const r = await $.get<any>(s.endpoint, { page: 1, pageSize: 1 });
          counts[s.match] = r.total ?? (Array.isArray(r) ? r.length : 0);
        } catch { counts[s.match] = 0; }
      }));
      return counts;
    },
    staleTime: 60_000,
  });

  const { data: myCustomers } = useQuery({
    queryKey: ['sidebar-my-customers'],
    enabled: canViewSalesDomain,
    queryFn: () => $.get<any>('/customers', { page: 1, pageSize: 1, owner: 'me' }),
    staleTime: 60_000,
  });

  // "我的客户" and "客户管理" used to point to the same /customers page (duplicate).
  // Keep only one top-level entry; the 8-stage list below covers detailed lookups.
  const topLinks: NavLink[] = [
    // #1 工作台：所有人始终显示（academic 走 /academic/dashboard）
    { key: isAcademic ? '/academic/dashboard' : '/dashboard', label: '工作台', icon: Icon.home },
    // #2 消息：im:view（6 角色都有）
    { key: '/im', label: '消息', icon: Icon.message, requirePerm: 'im:view', count: combinedUnread > 0 ? combinedUnread : undefined, unread: true },
  ];

  // v1.5 P2：文案保留（manager 看自己团队），按权限过滤
  const isManager = user.role === 'manager';
  const teamPrefix = isManager ? '我的团队' : '';
  const dataGroupLinks: NavLink[] = [
    // #11 公海池：customer:view（sales 也能看自己池）
    { key: '/customers/pool', label: '公海池', icon: Icon.pool, requirePerm: 'customer:view' },
    // #12 团队：与路由 /team 的 requirePermissions='user:view' 对齐（评审:原 team:view 致 academic_director 见菜单却被路由弹回）
    { key: '/team', label: '团队', icon: Icon.team, requirePerm: 'user:view' },
    // #13 数据报表：report:view_all
    { key: '/director', label: '数据报表', icon: Icon.chart, requirePerm: 'report:view_all' },
    // #14 数据看板：team OR all
    { key: '/reports', label: `${teamPrefix}数据看板`, icon: Icon.chart, requireAny: ['report:view_team', 'report:view_all'] },
    // #15 投流大盘
    { key: '/marketing/dashboard', label: `${teamPrefix}投流大盘`, icon: Icon.chart, requirePerm: 'marketing:dashboard_view' },
    // #16 投流线索
    { key: '/marketing/leads', label: '投流线索', icon: Icon.audit, requirePerm: 'lead:view' },
    // #17 员工绩效：manager 看团队成员绩效合理（保留 view_team OR all），只 hide sales（sales 有 view_team 但不该看管理者视图）
    { key: '/staff-performance', label: '员工绩效', icon: Icon.chart,
      requireAny: ['report:view_team', 'report:view_all'],
      hide: user.role === 'sales' /* role 兜底：sales 有 view_team（seed 0013），但员工绩效是管理者视图，无 sales 专属排除 perm */ },
    // 薪酬阶段3：薪酬看板/发薪台账（payroll:view = admin/director/manager；sales 走工作台个人激励卡）
    { key: '/salary/team-board', label: '薪酬看板', icon: Icon.chart, requirePerm: 'payroll:view' },
  ];

  const toolGroupLinks: NavLink[] = [
    // #18 AI 教练
    { key: '/ai-coach', label: 'AI 教练', icon: Icon.robot, requirePerm: 'ai:coach_view' },
    // #19 审批中心
    { key: '/approvals', label: '审批中心', icon: Icon.audit, requirePerm: 'approval:view' },
    // #20 救援列表
    { key: '/rescue', label: '救援列表', icon: Icon.alert, requirePerm: 'rescue:view' },
    // #21/22/23 数据导入（admin/director / manager / sales 各自路径不同；用 requirePerm + 路径前缀双重判断）
    { key: '/admin/data-import', label: '数据导入', icon: Icon.audit,
      hide: !['admin', 'director'].includes(user.role), requirePerm: 'import:run' },
    { key: '/team/data-import', label: '数据导入', icon: Icon.audit,
      hide: user.role !== 'manager', requirePerm: 'import:run' },
    { key: '/import', label: '我的导入', icon: Icon.audit,
      hide: user.role !== 'sales', requirePerm: 'import:screenshots' },
    // #24/25 业绩目标
    { key: '/admin/sales-targets', label: '业绩目标', icon: Icon.chart,
      hide: !['admin', 'director'].includes(user.role), requirePerm: 'sales_targets:edit' },
    { key: '/manager/sales-targets', label: '业绩目标', icon: Icon.chart,
      hide: user.role !== 'manager', requirePerm: 'sales_targets:view' },
  ];

  // v1.2 fix(nav)：教务流程组单独抽出，inline 渲染在客户流程紧下方
  // 默认折叠（未路由到 /academic/* 时）；折叠状态 localStorage 独立 key 'academic'
  const academicGroup = {
    key: 'academic',
    title: '教务流程',
    links: [
      // v1.5 P2 教务流程组（v1.2-rc2 原顺序保持）
      // #26 学员对接
      { key: '/academic/students', label: '学员对接', icon: Icon.users, requirePerm: 'academic:view' },
      // #27 教务总表
      { key: '/academic/overview', label: '教务总表', icon: Icon.chart, requirePerm: 'academic:view_overview' },
      // #28 学员任务管理
      { key: '/academic/tasks', label: '学员任务管理', icon: Icon.audit,
        requireAny: ['academic:manage_assignments', 'academic:view'] },
      // #29 教务日报：三向 OR（用户拍板 A）
      { key: '/academic/reports', label: '教务日报', icon: Icon.audit,
        requireAny: ['academic:submit_report', 'academic:approve_report', 'academic:view_overview'] },
      // #30 教务仪表盘
      { key: '/academic/dashboard', label: '教务仪表盘', icon: Icon.chart, requirePerm: 'academic:view' },
    ] as NavLink[],
  };

  // P5 侧栏分组：飞书系统级风格 — 仅 title 文字，无 emoji（emoji 由 .nav-section-title CSS 接管视觉）
  const adminGroups: Array<{ key: string; title: string; icon: string; links: NavLink[] }> = [
    { key: 'data',   title: '数据看板',   icon: '', links: dataGroupLinks },
    { key: 'tools',  title: '销售工具',   icon: '', links: toolGroupLinks },
    {
      key: 'team', title: '团队管理', icon: '',
      links: [
        // #31 用户管理：用 user:create（admin/director 有，manager 无；干净排除 manager）
        // manager 的 user:view 是业务权限（Leads/主管池选 sales），不代表能进用户管理页
        { key: '/admin/users', label: '用户管理', icon: Icon.users, requirePerm: 'user:create' },
        // #32 团队管理：team:manage 保留 + role 兜底（manager 真有 team:manage 是业务权限，
        // 但团队管理页只 admin/director；无页面专属权限点，后端 GET /admin/teams 等用 team:manage 真防线还在）
        { key: '/admin/teams', label: '团队管理', icon: Icon.users, requirePerm: 'team:manage',
          hide: !['admin', 'director'].includes(user.role) },
        // #33 离职日志
        { key: '/admin/transfer-logs', label: '离职日志', icon: Icon.audit, requirePerm: 'user:deactivate' },
      ],
    },
    {
      key: 'lifecycle', title: '客户运营', icon: '',
      links: [
        // #34 监管中心
        { key: '/admin/lead-monitor-center', label: '监管中心', icon: Icon.chart, requirePerm: 'customer:view_all_pool' },
        // #35 储备公海
        { key: '/admin/reserve-pool', label: '储备公海', icon: Icon.pool, requirePerm: 'customer:view_all_pool' },
        // #36 作废审批
        { key: '/manager/voidage-requests', label: '作废审批', icon: Icon.alert, requirePerm: 'customer:view_all_pool' },
        // #37 客户申请
        { key: '/manager/customer-creation-requests', label: '客户申请', icon: Icon.audit, requirePerm: 'approval:approve' },
        // #38 群发话术
        { key: '/manager/broadcast-campaigns', label: '群发话术', icon: Icon.audit,
          requireAny: ['broadcast:run', 'broadcast:view'] },
      ],
    },
    {
      key: 'marketing', title: '投流营销', icon: '',
      links: [
        // #39 投流账号
        { key: '/marketing/accounts', label: '投流账号', icon: Icon.settings, requirePerm: 'marketing:account_manage' },
        // v1.5：聚光 API 配置（OAuth + 自动拉花费）— 替代手填
        { key: '/marketing/xhs-oauth-config', label: '聚光 API 配置', icon: Icon.settings, requirePerm: 'marketing:account_manage' },
        // v1.5 P4：笔记排行榜（按 fee/leads/ROI 看哪篇笔记效果好）
        { key: '/marketing/xhs-notes', label: '笔记排行榜', icon: Icon.chart, requirePerm: 'marketing:dashboard_view' },
        // #40 分配规则：与路由 requirePermissions='lead:routing_view' 对齐（评审:原 configure_routing 致 manager 见菜单却被弹回）
        { key: '/admin/lead-routing-config', label: '分配规则', icon: Icon.settings, requirePerm: 'lead:routing_view' },
        // #41 投流对账
        { key: '/marketing/reconcile', label: '投流对账', icon: Icon.audit, requirePerm: 'marketing:reconcile' },
        // #42 手动录入
        { key: '/marketing/reconcile#manual', label: '└ 手动录入', icon: Icon.audit, requirePerm: 'marketing:reconcile' },
      ],
    },
    {
      key: 'finance', title: '财务', icon: '',
      links: [
        // #43 财务工作台：invoice:approve 保留 + role 兜底（manager 真有 invoice:approve 是业务权限，
        // 但财务工作台只 admin/director；无页面专属权限点，后端真防线在）
        { key: '/admin/finance-workbench', label: '财务工作台', icon: Icon.audit,
          requirePerm: 'invoice:approve',
          // bug收尾批#5：财务总监(directorType='finance'，程华庆 academic_director)也显示菜单(原角色白名单把他挡了)
          hide: !(['admin', 'director'].includes(user.role) || user.directorType === 'finance') },
      ],
    },
    {
      key: 'config', title: '系统配置', icon: '',
      links: [
        // #44 管理控制台（v1.5 P2-7：configs_view → configs_manage，仅 admin/director；
        //    manager 之前有 configs_view 误见此入口）
        { key: '/admin/control-panel', label: '管理控制台', icon: Icon.settings, requirePerm: 'system:configs_manage' },
        // #45 测试中心
        { key: '/admin/test-center', label: '测试中心', icon: Icon.settings, requirePerm: 'system:test_center' },
      ],
    },
  ];

  const isActive = (k: string) => {
    if (k === '/dashboard') return path === '/' || path === '/dashboard';
    return path === k || path.startsWith(k + '/');
  };

  const isStageActive = (s: typeof STAGES[number]) => path === s.match || path.startsWith(s.match + '/');

  // Global keyboard shortcuts — work everywhere except when typing in inputs.
  // N just dispatches the open-customer event — the modal is mounted at the app root
  // (see AppLayout below), so it works from any page without navigating away.
  useShortcuts({
    n: () => window.dispatchEvent(new CustomEvent('huanke:open-new-customer')),
    '0': () => nav('/dashboard'),
    '`': () => nav('/dashboard'),
    '1': () => nav('/customers'),
    '2': () => nav('/follow-ups'),
    '3': () => nav('/contracts'),
    '4': () => nav('/payments'),   // 流转理顺：④收款
    '5': () => nav('/invoices'),   // ⑤发票
    '6': () => nav('/shipments'),
    '7': () => nav('/materials'),
    '8': () => nav('/orders'),
  });

  const userMenu = {
    items: [
      { key: 'profile', label: '个人设置' },
      { key: 'logout', label: '退出登录', danger: true },
    ],
    onClick: ({ key }: any) => {
      if (key === 'profile') nav('/settings');
      if (key === 'logout') { logout(); nav('/login'); }
    },
  };

  // v1.5 P2：菜单可见性 = !hide && requirePerm 满足 && requireAny 满足
  // hide 仍保留（处理路由前缀差异：admin/data-import vs team/data-import 文案相同但路径不同）
  const visible = (l: NavLink): boolean => {
    if (l.hide) return false;
    if (l.requirePerm && !perms.has(l.requirePerm)) return false;
    if (l.requireAny && !perms.hasAny(l.requireAny)) return false;
    return true;
  };

  const renderLink = (l: NavLink) => (
    <div
      key={l.key}
      className={`nav-item ${isActive(l.key) ? 'active' : ''} ${l.unread && imPulse ? 'im-pulse' : ''}`}
      onClick={() => nav(l.key)}
    >
      {l.icon}
      <span className="nav-label">{l.label}</span>
      {typeof l.count === 'number' && (
        l.unread
          ? <span className="nav-unread-badge">{l.count > 99 ? '99+' : l.count}</span>
          : <span className="nav-count">{l.count}</span>
      )}
    </div>
  );

  return (
    <div className="hk-app">
      <header className="topbar">
        {isMobile && (
          <button
            className="icon-btn topbar-menu-btn"
            onClick={() => setDrawerOpen((v) => !v)}
            aria-label="菜单"
            data-tip="菜单"
          >
            <MenuOutlined />
          </button>
        )}
        <div className="brand" onClick={() => nav('/')}>
          <div className="brand-logo">HK</div>
          <div className="brand-name">HuanKe-CRM</div>
        </div>
        <GlobalSearch />
        <div className="topbar-actions">
          <button
            className="icon-btn"
            onClick={() => window.dispatchEvent(new CustomEvent('huanke:open-new-customer'))}
            data-tip="新增客户 · 按 N"
          >
            {Icon.plus}
          </button>
          <button className="icon-btn" onClick={() => nav('/dashboard')} data-tip="日程 · 按 0">
            {Icon.calendar}
          </button>
          <button className="icon-btn" onClick={() => nav('/notifications')} data-tip="消息通知">
            {Icon.bell}
            {(unread?.count ?? 0) > 0 && <span className="badge">{unread!.count}</span>}
          </button>
          <div className="topbar-divider" />
          <Dropdown menu={userMenu}>
            <div className="topbar-user">
              {user.avatarUrl && !avatarErr ? (
                <img src={user.avatarUrl} alt={user.realName}
                     className="user-avatar"
                     style={{ objectFit: 'cover' }}
                     onError={() => setAvatarErr(true)} />
              ) : (
                <div className="user-avatar">{user.realName?.charAt(0) ?? 'U'}</div>
              )}
              <span className="user-name-small">{user.realName}</span>
            </div>
          </Dropdown>
        </div>
      </header>

      <div className="hk-body">
        <aside className={`sidebar ${isMobile && drawerOpen ? 'open' : ''}`}>
          <div className="sidebar-scroll">
            {topLinks.filter(visible).map(renderLink)}

            {/* 客户流程：可折叠组（默认展开）
                v1.5 P2 修正 #3：移除容器层 !isAcademic 整组隐藏；改为每个 stage 按 requirePerm 独立 filter
                效果：academic 客户流程组里只剩"学员资料"一项（material:view 放行），不会整组没；
                      sales/manager/admin/director 8 项全见；其它角色看不到不该看的 stage */}
            {(() => {
              const groupKey = 'customer-flow';
              // 先按 perm/role 过滤可见 stages
              const visibleStages = STAGES.filter((s) => {
                if (s.hideForRoles && s.hideForRoles.includes(user.role)) return false;
                if (s.requirePerm && !perms.has(s.requirePerm)) return false;
                return true;
              });
              // 一项都看不到 → 整组隐藏（兜底）
              if (visibleStages.length === 0) return null;
              const explicit = openGroups[groupKey];
              const isOpen = explicit !== undefined ? explicit : true;
              return (
                <div className="nav-section">
                  <div
                    className={`nav-section-title ${isOpen ? 'expanded' : ''}`}
                    onClick={() => setOpenGroups((s) => ({ ...s, [groupKey]: !isOpen }))}
                  >
                    <svg
                      width="14" height="14" viewBox="0 0 24 24" fill="none"
                      stroke="currentColor" strokeWidth="1.5"
                      strokeLinecap="round" strokeLinejoin="round"
                      style={{
                        transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                        transition: 'transform 240ms cubic-bezier(0.34, 1.56, 0.64, 1)',
                        flexShrink: 0,
                      }}
                    >
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                    <span>客户流程</span>
                  </div>
                  {isOpen && (
                    <div className="stage-list">
                      {visibleStages.map((s) => {
                        const active = isStageActive(s);
                        const count = stageCounts?.[s.match];
                        const urgent = s.num === 2 && (count ?? 0) > 5;
                        return (
                          <div
                            key={s.match}
                            className={`stage-item ${active ? 'active' : ''}`}
                            onClick={() => nav(s.match)}
                          >
                            <div className="stage-num">{s.num}</div>
                            <span className="stage-name">{s.name}</span>
                            {count != null && count > 0 && (
                              <span className={`stage-count ${urgent ? 'urgent' : ''}`}>{count}</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* v1.2 fix(nav)：教务流程组紧贴客户流程下方（默认折叠） */}
            {(() => {
              const visibleLinks = academicGroup.links.filter(visible);
              if (visibleLinks.length === 0) return null;
              // openGroups['academic'] 未定义 → 跟随路由：当前在 /academic/* 时展开，否则折叠
              const isOpen = openGroups[academicGroup.key] !== undefined
                ? openGroups[academicGroup.key]
                : visibleLinks.some((l) => isActive(l.key));
              return (
                <div className="nav-section" key="academic-flow">
                  <div
                    className={`nav-section-title ${isOpen ? 'expanded' : ''}`}
                    onClick={() => setOpenGroups((s) => ({ ...s, [academicGroup.key]: !isOpen }))}
                  >
                    <svg
                      width="14" height="14" viewBox="0 0 24 24" fill="none"
                      stroke="currentColor" strokeWidth="1.5"
                      strokeLinecap="round" strokeLinejoin="round"
                      style={{
                        transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                        transition: 'transform 240ms cubic-bezier(0.34, 1.56, 0.64, 1)',
                        flexShrink: 0,
                      }}
                    >
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                    <span>{academicGroup.title}</span>
                  </div>
                  {isOpen && visibleLinks.map(renderLink)}
                </div>
              );
            })()}

            {adminGroups.map((g) => {
              const visibleLinks = g.links.filter(visible);
              if (visibleLinks.length === 0) return null;
              // 单一来源：openGroups[g.key] 未定义 → 跟随路由（默认展开当前页所在组）
              //          openGroups[g.key] 已定义 → 用户手动控制，覆盖路由
              const isOpen = openGroups[g.key] !== undefined
                ? openGroups[g.key]
                : visibleLinks.some((l) => isActive(l.key));
              return (
                <div key={g.key} className="nav-section">
                  <div
                    className={`nav-section-title ${isOpen ? 'expanded' : ''}`}
                    onClick={() => setOpenGroups((s) => ({ ...s, [g.key]: !isOpen }))}
                  >
                    <svg
                      width="14" height="14" viewBox="0 0 24 24" fill="none"
                      stroke="currentColor" strokeWidth="1.5"
                      strokeLinecap="round" strokeLinejoin="round"
                      style={{
                        transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                        transition: 'transform 240ms cubic-bezier(0.34, 1.56, 0.64, 1)',
                        flexShrink: 0,
                      }}
                    >
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                    <span>{g.title}</span>
                  </div>
                  {isOpen && visibleLinks.map(renderLink)}
                </div>
              );
            })}
          </div>
          {/* Footer 设置 only shown for non-admin (admin already has "系统配置"
              in the system section above — avoids duplicate entry). */}
          {user.role !== 'admin' && (
            <div className="sidebar-footer">
              <div
                className="nav-item"
                onClick={() => nav('/settings')}
                data-tip="个人设置（资料/密码/通知/安全）"
              >
                {Icon.settings}
                <span className="nav-label">设置</span>
              </div>
            </div>
          )}
        </aside>

        {isMobile && drawerOpen && (
          <div className="sidebar-backdrop" onClick={() => setDrawerOpen(false)} />
        )}

        <div className="workspace">
          <main className="work-main">
            <Outlet />
          </main>
        </div>
      </div>

      {canCreate && (
        <NewCustomerModal
          open={showNewCustomer}
          onClose={() => setShowNewCustomer(false)}
        />
      )}
    </div>
  );
}
